import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY, logoutAppUser } from "./appAuthSlice";
import {
  mergeCasinoGamesInList,
  upsertCasinoGameInList,
  getCasinoGameId,
  getBetGameId,
  getPendingBetsForGame,
  upsertPendingBetInList,
} from "../utils/casinoHelpers";
import {
  runCasinoCreate,
  runCasinoJoin,
  runCasinoCancel,
  runCasinoResolve,
} from "./casinoSocket";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

export const fetchCasinoGames = createAsyncThunk(
  "casino/fetchGames",
  async ({ page = 1, limit = 40 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/casino", { params: { page, limit } });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Spin games"));
    }
  }
);

export const casinoCreate = createAsyncThunk(
  "casino/create",
  async (payload, thunkApi) => runCasinoCreate(payload, thunkApi)
);

export const fetchUserPendingCasinoBets = createAsyncThunk(
  "casino/fetchPendingBets",
  async (userId, { rejectWithValue }) => {
    if (!userId) return [];
    try {
      const res = await api.get(`/api/casino/pending/${userId}`);
      return res.data?.pendingGames || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load your Bond Spin bets"));
    }
  }
);

export const casinoJoin = createAsyncThunk(
  "casino/join",
  async (payload, thunkApi) => runCasinoJoin(payload, thunkApi)
);

export const casinoCancel = createAsyncThunk(
  "casino/cancel",
  async (payload, thunkApi) => runCasinoCancel(payload, thunkApi)
);

export const casinoResolve = createAsyncThunk(
  "casino/resolve",
  async (payload, thunkApi) => runCasinoResolve(payload, thunkApi)
);

export const fetchCasinoPlayers = createAsyncThunk(
  "casino/fetchPlayers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/casino/players");
      return res.data?.players || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Spin players"));
    }
  }
);

const initialState = {
  games: [],
  pendingBets: [],
  players: [],
  pagination: {
    page: 1,
    limit: 40,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
  },
  loading: false,
  loadingPlayers: false,
  actionLoading: false,
  status: "idle",
  error: null,
};

const casinoSlice = createSlice({
  name: "casino",
  initialState,
  reducers: {
    upsertCasinoGame: (state, { payload }) => {
      if (!payload) return;
      state.games = upsertCasinoGameInList(state.games, payload);
    },
    removeCasinoGame: (state, { payload }) => {
      const id = payload ? String(payload) : "";
      if (!id) return;
      state.games = state.games.filter((g) => getCasinoGameId(g) !== id);
    },
    upsertPendingBet: (state, { payload }) => {
      if (!payload) return;
      state.pendingBets = upsertPendingBetInList(state.pendingBets, payload);
    },
    removePendingBet: (state, { payload }) => {
      const id = payload ? String(payload) : "";
      if (!id) return;
      state.pendingBets = state.pendingBets.filter((b) => String(b._id) !== id);
    },
    resetCasinoState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCasinoGames.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCasinoGames.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.status = "succeeded";
        state.games = mergeCasinoGamesInList([], payload?.games || []);
        state.pagination = payload?.pagination || state.pagination;
      })
      .addCase(fetchCasinoGames.rejected, (state, { payload }) => {
        state.loading = false;
        state.status = "failed";
        state.error = payload || "Failed to load Bond Spin games";
      })
      .addCase(casinoCreate.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(casinoCreate.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(casinoCreate.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload?.message || payload || "Could not create Bond Spin table";
      })
      .addCase(fetchUserPendingCasinoBets.fulfilled, (state, { payload }) => {
        state.pendingBets = payload || [];
      })
      .addCase(casinoJoin.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(casinoJoin.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        if (payload?.bet) {
          state.pendingBets = upsertPendingBetInList(state.pendingBets, payload.bet);
        }
      })
      .addCase(casinoJoin.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error =
          (typeof payload === "object" && payload?.message) ||
          (typeof payload === "string" ? payload : null) ||
          "Could not reverse ticket";
      })
      .addCase(casinoCancel.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(casinoCancel.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        if (payload?.betId) {
          state.pendingBets = state.pendingBets.filter((b) => String(b._id) !== String(payload.betId));
        }
      })
      .addCase(casinoCancel.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload?.message || payload || "Could not cancel bet";
      })
      .addCase(casinoResolve.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(casinoResolve.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        if (payload?.bet?._id) {
          state.pendingBets = state.pendingBets.filter(
            (b) => String(b._id) !== String(payload.bet._id)
          );
        }
      })
      .addCase(casinoResolve.rejected, (state, { payload }) => {
        state.actionLoading = false;
        state.error = payload?.message || payload || "Could not resolve spin";
      })
      .addCase(fetchCasinoPlayers.pending, (state) => {
        state.loadingPlayers = true;
      })
      .addCase(fetchCasinoPlayers.fulfilled, (state, { payload }) => {
        state.loadingPlayers = false;
        state.players = payload || [];
      })
      .addCase(fetchCasinoPlayers.rejected, (state) => {
        state.loadingPlayers = false;
        state.players = [];
      })
      .addCase(logoutAppUser.fulfilled, () => initialState);
  },
});

export const { upsertCasinoGame, removeCasinoGame, upsertPendingBet, removePendingBet, resetCasinoState } =
  casinoSlice.actions;
export default casinoSlice.reducer;

export const selectCasinoGames = (state) => state.casino?.games || [];
export const selectCasinoLoading = (state) => state.casino?.loading;
export const selectCasinoActionLoading = (state) => state.casino?.actionLoading;
export const selectCasinoError = (state) => state.casino?.error;

export const selectCasinoGameById = (state, gameId) => {
  if (!gameId) return null;
  const id = String(gameId);
  return state.casino.games.find((g) => getCasinoGameId(g) === id) || null;
};

export const selectPendingCasinoBets = (state) => state.casino?.pendingBets || [];
export const selectCasinoPlayers = (state) => state.casino?.players || [];
export const selectCasinoLoadingPlayers = (state) => state.casino?.loadingPlayers;

export const selectPendingBetForGame = (state, gameId) => {
  if (!gameId) return null;
  const id = String(gameId);
  return (
    state.casino.pendingBets.find(
      (bet) => bet?.status === "Pending" && getBetGameId(bet) === id
    ) || null
  );
};

export const selectPendingBetsForGame = (state, gameId) => {
  if (!gameId) return [];
  return getPendingBetsForGame(state.casino?.pendingBets || [], gameId);
};
